import React, {useMemo, useState} from 'react';

import {ConfirmationModal, Select} from '@/components';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';

type TransferOwnerModalProps = {
  isOpen: boolean;
  dataCy?: string;
  onClose: () => void;
  onConfirm: (newOwnerUserId: string) => Promise<boolean | void>;
  isLoading?: boolean;
  members: AfriexBusinessUser[];
};

const TransferOwnerModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  dataCy,
  members,
}: TransferOwnerModalProps) => {
  const [newOwnerUserId, setNewOwnerUserId] = useState('');

  const memberOptions = useMemo(
    () =>
      members.map((member) => ({
        value: member.id,
        key: member.email
          ? `${member.name?.fullName} (${member.email})`
          : member.name?.fullName,
      })),
    [members],
  );

  const handleConfirm = async () => {
    if (!newOwnerUserId) {
      return;
    }
    const result = await onConfirm(newOwnerUserId);
    // Keep the modal open on failure (error already surfaced via toast).
    if (result !== false) {
      setNewOwnerUserId('');
      onClose();
    }
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      title="Transfer ownership"
      label="The selected member becomes the new organization owner and the current owner is demoted to Staff. Choose the member to transfer ownership to."
      cancelAction={onClose}
      cancelActionLabel="Cancel"
      confirmAction={handleConfirm}
      confirmActionLabel="Transfer ownership"
      loading={!!isLoading}
      loadingLabel="Transferring..."
      width="w-96"
      dataCy={dataCy}
    >
      <Select
        dataCy={dataCy}
        label="New owner"
        placeholder={
          memberOptions.length ? 'Select a member' : 'No eligible members'
        }
        value={newOwnerUserId}
        options={memberOptions}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setNewOwnerUserId(e.target.value)
        }
      />
    </ConfirmationModal>
  );
};

export default TransferOwnerModal;
